export interface DocumentTypeField {
  name: string;
  description: string;
}

export interface DocumentTypeFaq {
  q: string;
  a: string;
}

export interface DocumentTypeLink {
  href: string;
  title: string;
  body: string;
}

export interface DocumentTypeSampleLineItem {
  sku: string;
  description: string;
  quantity: number;
  unitPrice: number;
  amount: number;
}

export interface DocumentTypeSample {
  vendorName: string;
  customerName: string;
  invoiceNumber: string;
  invoiceDate: string;
  dueDate: string;
  currency: string;
  subtotal: number;
  tax: number;
  total: number;
  lineItems: DocumentTypeSampleLineItem[];
}

export interface DocumentTypeMeta {
  slug: string;
  docType: string;
  navTitle: string;
  eyebrow: string;
  outcome: string;
  metaTitle: string;
  metaDescription: string;
  h1: string;
  subhead: string;
  schema: DocumentTypeField[];
  sample: DocumentTypeSample;
  apiSnippet: {
    curl: string;
    node: string;
  };
  relatedLinks: DocumentTypeLink[];
  faqs: DocumentTypeFaq[];
}

export interface SeoMeta {
  title: string;
  description: string;
  canonical: string;
}

export const DOCUMENT_TYPES: DocumentTypeMeta[] = [
  {
    slug: 'invoice',
    docType: 'Invoice',
    navTitle: 'Invoice',
    eyebrow: 'By document type / Invoice',
    outcome: 'Turn PDF invoices into structured rows with vendor, totals, and line items.',
    metaTitle: 'Invoice Parser API - extract data from PDF invoices | okraPDF',
    metaDescription:
      'Pull vendor, totals, and line items from any PDF invoice with one API call. Free tier, per-page pricing, no rule-drawing.',
    h1: 'Invoice parser API',
    subhead:
      'Send any PDF invoice. Get back vendor, dates, totals, and line items as typed JSON. No zone-drawing, no per-vendor rules.',
    schema: [
      { name: 'vendor_name', description: 'Issuer or supplier name.' },
      { name: 'invoice_number', description: 'Vendor-assigned invoice ID.' },
      { name: 'invoice_date', description: 'ISO date the invoice was issued.' },
      { name: 'due_date', description: 'ISO date payment is due.' },
      { name: 'subtotal', description: 'Pre-tax total.' },
      { name: 'tax', description: 'Tax amount.' },
      { name: 'total', description: 'Final amount due.' },
      {
        name: 'line_items',
        description: 'Array of description, quantity, unit_price, and amount values.',
      },
    ],
    sample: {
      vendorName: 'Acme Corp',
      customerName: 'Globex Industries',
      invoiceNumber: 'INV-2026-1048',
      invoiceDate: '2026-05-05',
      dueDate: '2026-06-04',
      currency: 'USD',
      subtotal: 450,
      tax: 37.13,
      total: 487.13,
      lineItems: [
        {
          sku: 'DOC-API',
          description: 'PDF extraction API subscription',
          quantity: 1,
          unitPrice: 240,
          amount: 240,
        },
        {
          sku: 'OCR-BATCH',
          description: 'Batch OCR page bundle',
          quantity: 500,
          unitPrice: 0.04,
          amount: 20,
        },
        {
          sku: 'SUPPORT',
          description: 'Implementation support hour',
          quantity: 2,
          unitPrice: 95,
          amount: 190,
        },
      ],
    },
    apiSnippet: {
      curl: `curl -X POST https://api.okrapdf.com/v1/workflows/invoice-extraction/runs \\
  -H "Authorization: Bearer $OKRA_API_KEY" \\
  -F "file=@invoice.pdf"`,
      node: `import okraPDF from "@okrapdf/sdk";

const okra = new okraPDF({ apiKey: process.env.OKRA_API_KEY });
const run = await okra.runInvoiceExtraction({
  inputs: [{ file: fs.createReadStream("invoice.pdf") }],
});

const firstRow = run.rows[0];`,
    },
    relatedLinks: [
      {
        href: '/tools/pdf-to-json?template=invoice',
        title: 'Full PDF to JSON extractor',
        body: 'Use a browser-based extractor when you want to test a PDF before wiring the API.',
      },
      {
        href: '/tools/pdf-to-link',
        title: 'API keys and hosting',
        body: 'Mint a key, upload documents, and call extraction from your backend.',
      },
      {
        href: '/use-cases/bank-statement-extraction',
        title: 'Bank statement extraction',
        body: 'A related accounting workflow for statement rows, balances, and CSV export.',
      },
    ],
    faqs: [
      {
        q: 'Does it work on scanned invoices?',
        a: 'Yes. OCR runs automatically when the PDF has no text layer. The response keeps the same JSON shape.',
      },
      {
        q: 'How does it handle multiple invoices in one PDF?',
        a: 'Each detected invoice becomes a row in the response.',
      },
      {
        q: 'Can I customize the schema?',
        a: 'Yes. Use Data Tables to define the columns you need, then send invoices into that table.',
      },
      {
        q: 'What about non-English invoices?',
        a: 'Field detection is multilingual. Output values stay in the source language unless you add a translation step.',
      },
      {
        q: 'How is this priced?',
        a: 'Extraction is priced per page. The free tier covers the first 100 pages.',
      },
    ],
  },
  {
    slug: 'purchase-order',
    docType: 'Purchase order',
    navTitle: 'Purchase order',
    eyebrow: 'By document type / Purchase order',
    outcome: 'Pull PO numbers, ship dates, vendor terms, and ordered SKUs into your ERP.',
    metaTitle: 'Purchase Order Extraction API - parse PDF POs | okraPDF',
    metaDescription:
      'Extract PO number, ship-by date, vendor, payment terms, and line items from PDF purchase orders with one API call. Typed JSON out. No zone-drawing.',
    h1: 'Purchase order extraction API',
    subhead:
      'Send any PDF purchase order. The extraction API parses the layout and returns PO number, ship-by date, buyer, vendor, payment terms, and ordered SKUs as typed JSON.',
    schema: [
      { name: 'po_number', description: 'Buyer-assigned purchase order ID.' },
      { name: 'vendor_name', description: 'Supplier the order is sent to.' },
      { name: 'buyer_name', description: 'Company placing the order.' },
      { name: 'ship_to', description: 'Delivery address for the ordered goods.' },
      { name: 'order_date', description: 'ISO date the PO was created.' },
      { name: 'ship_date', description: 'ISO date goods are expected to ship.' },
      { name: 'payment_terms', description: 'Net 30, Net 60, COD, etc.' },
      { name: 'currency', description: 'ISO 4217 currency code for the order.' },
      { name: 'subtotal', description: 'Pre-tax total of all ordered items.' },
      { name: 'total', description: 'Final amount including tax and shipping.' },
      {
        name: 'line_items',
        description: 'Array of sku, description, quantity, unit_price, and amount values.',
      },
    ],
    sample: {
      vendorName: 'Initech Manufacturing',
      customerName: 'Globex Industries',
      invoiceNumber: 'PO-2026-9921',
      invoiceDate: '2026-05-12',
      dueDate: '2026-06-14',
      currency: 'USD',
      subtotal: 8400,
      tax: 672,
      total: 9072,
      lineItems: [
        {
          sku: 'BRACKET-44',
          description: 'Steel mounting bracket, type B',
          quantity: 200,
          unitPrice: 18,
          amount: 3600,
        },
        {
          sku: 'BOLT-M8',
          description: 'M8 hex bolt, zinc plated, 30mm',
          quantity: 1000,
          unitPrice: 0.4,
          amount: 400,
        },
        {
          sku: 'PANEL-AL',
          description: 'Aluminum panel, 600x400mm',
          quantity: 80,
          unitPrice: 55,
          amount: 4400,
        },
      ],
    },
    apiSnippet: {
      curl: `# 1. Upload the PDF
DOC_ID=$(curl -s -X POST https://api.okrapdf.com/v1/documents \\
  -H "Authorization: Bearer $OKRA_API_KEY" \\
  -F "file=@purchase-order.pdf" | jq -r '.id')

# 2. Extract with a purchase-order JSON Schema
curl -X POST "https://api.okrapdf.com/v1/documents/$DOC_ID/structured-output" \\
  -H "Authorization: Bearer $OKRA_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "schema": {
      "type": "object",
      "properties": {
        "po_number": { "type": "string" },
        "vendor_name": { "type": "string" },
        "ship_date": { "type": "string", "format": "date" },
        "line_items": { "type": "array" }
      }
    }
  }'`,
      node: `import okraPDF from "@okrapdf/sdk";

const okra = new okraPDF({ apiKey: process.env.OKRA_API_KEY });

const doc = await okra.documents.create({
  file: fs.createReadStream("purchase-order.pdf"),
});
const result = await okra.documents.structuredOutput(doc.id, {
  schema: {
    type: "object",
    properties: {
      po_number: { type: "string" },
      vendor_name: { type: "string" },
      ship_date: { type: "string", format: "date" },
      line_items: { type: "array" },
    },
  },
});`,
    },
    relatedLinks: [
      {
        href: '/tools/pdf-to-json?template=purchase-order',
        title: 'Full PDF to JSON extractor',
        body: 'Use the browser extractor to dry-run a PO before wiring the API.',
      },
      {
        href: '/document-types/invoice',
        title: 'Invoice parser',
        body: 'POs and their matching invoices use the same line-item shape.',
      },
      {
        href: '/tools/pdf-to-link',
        title: 'API keys and hosting',
        body: 'Mint a key, upload documents, and call extraction from your backend.',
      },
    ],
    faqs: [
      {
        q: 'Is this a purchase order parser or an extraction API?',
        a: 'Both — they are the same endpoint here. okraPDF parses the PDF layout, then the extraction API returns typed JSON that matches the JSON Schema you send. No templates, no zone coordinates, no per-vendor rules to maintain.',
      },
      {
        q: 'Does it work on scanned POs?',
        a: 'Yes. OCR runs automatically when the PDF has no text layer, so faxed or photographed purchase orders extract the same way as digital ones.',
      },
      {
        q: 'Can I customize the line-item schema?',
        a: 'Yes. The schema in the request is plain JSON Schema — add fields like unit, tax_rate, requested_delivery_date, or a GL code and the same endpoint returns them per line item.',
      },
      {
        q: 'What happens with fields the PO does not contain?',
        a: 'Missing fields come back null rather than guessed, so your ERP import can branch on presence instead of validating hallucinated values.',
      },
      {
        q: 'Can I match POs to incoming invoices?',
        a: 'Yes. The PO and invoice schemas share `vendor_name` and line-item shape, which is enough to two-way match in your ERP.',
      },
      {
        q: 'How is this priced?',
        a: 'Extraction is priced per page. The free tier covers the first 100 pages.',
      },
    ],
  },
  {
    slug: 'bill-of-lading',
    docType: 'Bill of lading',
    navTitle: 'Bill of lading',
    eyebrow: 'By document type / Bill of lading',
    outcome: 'Carrier, consignee, ports, container IDs, and freight terms — typed and tagged.',
    metaTitle: 'Bill of Lading Parser API - BoL data extraction from PDFs | okraPDF',
    metaDescription:
      'Bill of lading parser and data extractor in one API call: carrier, shipper, consignee, ports, container and seal numbers, freight terms as typed JSON.',
    h1: 'Bill of lading parser and data extractor API',
    subhead:
      'Send any PDF bill of lading — ocean, truck, or rail, digital or scanned. The data extractor returns carrier, consignee, ports, container IDs, and freight terms as typed JSON.',
    schema: [
      { name: 'bol_number', description: 'Bill of lading reference number.' },
      { name: 'bol_date', description: 'ISO date the BoL was issued.' },
      { name: 'carrier', description: 'Shipping line or trucking company.' },
      { name: 'scac', description: 'Standard Carrier Alpha Code, when printed.' },
      { name: 'shipper', description: 'Party originating the shipment.' },
      { name: 'consignee', description: 'Receiving party.' },
      { name: 'vessel_voyage', description: 'Vessel name and voyage number on ocean BoLs.' },
      { name: 'origin_port', description: 'Port or terminal of loading.' },
      { name: 'destination_port', description: 'Port or terminal of discharge.' },
      { name: 'container_numbers', description: 'Array of container or trailer IDs.' },
      { name: 'seal_numbers', description: 'Array of seal IDs matched to containers.' },
      { name: 'freight_terms', description: 'Prepaid, collect, third-party, etc.' },
      {
        name: 'line_items',
        description: 'Array of description, weight, volume, and quantity values.',
      },
    ],
    sample: {
      vendorName: 'Pacific Maritime Lines',
      customerName: 'Globex Distribution Centers',
      invoiceNumber: 'BOL-2049-PMX',
      invoiceDate: '2026-05-10',
      dueDate: '2026-05-26',
      currency: 'USD',
      subtotal: 4200,
      tax: 0,
      total: 4200,
      lineItems: [
        {
          sku: 'CONT-PMX2049',
          description: '40ft container · 12,400 kg · electronics',
          quantity: 1,
          unitPrice: 2800,
          amount: 2800,
        },
        {
          sku: 'CONT-PMX2050',
          description: '40ft container · 9,800 kg · electronics',
          quantity: 1,
          unitPrice: 1400,
          amount: 1400,
        },
      ],
    },
    apiSnippet: {
      curl: `# 1. Upload the PDF
DOC_ID=$(curl -s -X POST https://api.okrapdf.com/v1/documents \\
  -H "Authorization: Bearer $OKRA_API_KEY" \\
  -F "file=@bol.pdf" | jq -r '.id')

# 2. Extract with a bill-of-lading JSON Schema
curl -X POST "https://api.okrapdf.com/v1/documents/$DOC_ID/structured-output" \\
  -H "Authorization: Bearer $OKRA_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "schema": {
      "type": "object",
      "properties": {
        "bol_number": { "type": "string" },
        "carrier": { "type": "string" },
        "consignee": { "type": "string" },
        "container_numbers": { "type": "array", "items": { "type": "string" } }
      }
    }
  }'`,
      node: `import okraPDF from "@okrapdf/sdk";

const okra = new okraPDF({ apiKey: process.env.OKRA_API_KEY });

const doc = await okra.documents.create({
  file: fs.createReadStream("bol.pdf"),
});
const result = await okra.documents.structuredOutput(doc.id, {
  schema: {
    type: "object",
    properties: {
      bol_number: { type: "string" },
      carrier: { type: "string" },
      consignee: { type: "string" },
      container_numbers: { type: "array", items: { type: "string" } },
    },
  },
});`,
    },
    relatedLinks: [
      {
        href: '/blog/bill-of-lading-ocr-benchmark/',
        title: 'Bill of lading OCR benchmark',
        body: 'We scored 24 parsers on 25 BoL PDFs — digital, scanned, and fax-quality.',
      },
      {
        href: '/tools/pdf-to-json?template=bill-of-lading',
        title: 'Full PDF to JSON extractor',
        body: 'Dry-run a BoL in the browser before wiring the API.',
      },
      {
        href: '/document-types/purchase-order',
        title: 'Purchase order parser',
        body: 'Match incoming BoLs back to the originating PO.',
      },
      {
        href: '/tools/pdf-to-link',
        title: 'API keys and hosting',
        body: 'Mint a key, upload documents, and call extraction from your backend.',
      },
    ],
    faqs: [
      {
        q: 'What is a bill of lading parser?',
        a: 'A bill of lading parser reads a BoL PDF or scan and turns it into structured data: the BoL number, carrier, shipper, consignee, ports, container and seal numbers, and cargo rows. It replaces manual keying into a TMS, ERP, or customs filing.',
      },
      {
        q: 'How accurate is it?',
        a: 'In our bill of lading OCR benchmark (24 parsers, 25 BoLs, a 21-field schema), okraPDF scored 96.9% field-level accuracy overall: 100% on digital and scanned BoLs and 84.2% on fax-quality pages. The methodology and the full leaderboard are public.',
      },
      {
        q: 'What does the bill of lading data extractor return?',
        a: 'Typed JSON matching the schema you send: BoL number and date, carrier and SCAC, shipper, consignee, vessel/voyage, ports, container and seal numbers, freight terms, and per-item weight and quantity rows — ready for your TMS or ERP.',
      },
      {
        q: 'Does it work on scanned or faxed BoLs?',
        a: 'Yes. Most BoLs arrive as scans, photos, or faxes. OCR runs automatically when the PDF has no text layer, and skewed or low-resolution pages are handled before extraction.',
      },
      {
        q: 'Ocean, truck, and rail bills of lading?',
        a: 'Yes. Straight, ocean, and inland BoLs share the core schema; fields that do not apply (like vessel_voyage on a truck BoL) come back null instead of guessed.',
      },
      {
        q: 'Master and house BoLs both supported?',
        a: 'Yes. The schema covers both; container_numbers may be empty on a master BoL.',
      },
      {
        q: 'Do carrier-specific layouts need templates?',
        a: 'No. The parser reads each PDF’s layout directly — no zone-drawing and no per-carrier template library to maintain as layouts change.',
      },
      {
        q: 'How is this priced?',
        a: 'Extraction is priced per page. The free tier covers the first 100 pages.',
      },
    ],
  },
  {
    slug: 'receipt',
    docType: 'Receipt',
    navTitle: 'Receipt',
    eyebrow: 'By document type / Receipt',
    outcome: 'Merchant, payment method, tax, and itemized totals from photos or PDFs.',
    metaTitle: 'Receipt Parser API - extract data from PDF receipts | okraPDF',
    metaDescription:
      'Turn PDF or photo receipts into typed rows with merchant, payment method, tax, total, and line items. One API call. Free tier.',
    h1: 'Receipt parser API',
    subhead:
      'Send any receipt — PDF, JPG, or PNG. Get merchant, payment method, tax, total, and itemized purchases as typed JSON.',
    schema: [
      { name: 'merchant', description: 'Store or vendor name on the receipt.' },
      { name: 'merchant_address', description: 'Street address of the merchant if printed.' },
      { name: 'transaction_date', description: 'ISO date of the purchase.' },
      { name: 'transaction_time', description: '24-hour time when printed.' },
      { name: 'payment_method', description: 'Card brand and last 4, cash, or wallet.' },
      { name: 'subtotal', description: 'Pre-tax total.' },
      { name: 'tax', description: 'Tax amount.' },
      { name: 'tip', description: 'Tip amount, if present.' },
      { name: 'total', description: 'Final amount charged.' },
      {
        name: 'line_items',
        description: 'Array of description, quantity, and amount values.',
      },
    ],
    sample: {
      vendorName: 'Bluebird Coffee · 5th Ave',
      customerName: 'Walk-in',
      invoiceNumber: 'TX-4421-A',
      invoiceDate: '2026-05-21',
      dueDate: '2026-05-21',
      currency: 'USD',
      subtotal: 48.4,
      tax: 4.2,
      total: 58.4,
      lineItems: [
        {
          sku: 'LATTE-12OZ',
          description: 'Vanilla latte, 12oz',
          quantity: 2,
          unitPrice: 6.5,
          amount: 13,
        },
        {
          sku: 'CROISSANT',
          description: 'Almond croissant',
          quantity: 3,
          unitPrice: 4.8,
          amount: 14.4,
        },
        {
          sku: 'SANDWICH',
          description: 'Turkey + brie sandwich',
          quantity: 2,
          unitPrice: 10.5,
          amount: 21,
        },
      ],
    },
    apiSnippet: {
      curl: `# 1. Upload the receipt (PDF or image)
DOC_ID=$(curl -s -X POST https://api.okrapdf.com/v1/documents \\
  -H "Authorization: Bearer $OKRA_API_KEY" \\
  -F "file=@receipt.jpg" | jq -r '.id')

# 2. Extract with a receipt JSON Schema
curl -X POST "https://api.okrapdf.com/v1/documents/$DOC_ID/structured-output" \\
  -H "Authorization: Bearer $OKRA_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "schema": {
      "type": "object",
      "properties": {
        "merchant": { "type": "string" },
        "transaction_date": { "type": "string", "format": "date" },
        "total": { "type": "number" },
        "line_items": { "type": "array" }
      }
    }
  }'`,
      node: `import okraPDF from "@okrapdf/sdk";

const okra = new okraPDF({ apiKey: process.env.OKRA_API_KEY });

const doc = await okra.documents.create({
  file: fs.createReadStream("receipt.jpg"),
});
const result = await okra.documents.structuredOutput(doc.id, {
  schema: {
    type: "object",
    properties: {
      merchant: { type: "string" },
      transaction_date: { type: "string", format: "date" },
      total: { type: "number" },
      line_items: { type: "array" },
    },
  },
});`,
    },
    relatedLinks: [
      {
        href: '/tools/pdf-to-json?template=receipt',
        title: 'Full PDF to JSON extractor',
        body: 'Dry-run a receipt in the browser before wiring the API.',
      },
      {
        href: '/document-types/invoice',
        title: 'Invoice parser',
        body: 'Expense reports often combine receipts with vendor invoices.',
      },
      {
        href: '/tools/pdf-to-link',
        title: 'API keys and hosting',
        body: 'Mint a key, upload documents, and call extraction from your backend.',
      },
    ],
    faqs: [
      {
        q: 'Does it work on phone photos of receipts?',
        a: 'Yes. Photos, scans, and PDFs all go through the same pipeline. Cropping and deskew are automatic.',
      },
      {
        q: 'Crumpled or faded thermal receipts?',
        a: 'OCR handles most thermal receipts. Severely faded text returns null on the affected fields rather than guessing.',
      },
      {
        q: 'How is this priced?',
        a: 'Extraction is priced per page. The free tier covers the first 100 pages.',
      },
    ],
  },
  {
    slug: 'bank-statement',
    docType: 'Bank statement',
    navTitle: 'Bank statement',
    eyebrow: 'By document type / Bank statement',
    outcome: 'Every transaction with date, payee, amount, and balance — even on scanned PDFs.',
    metaTitle: 'Bank Statement Parser API - extract transactions from PDFs | okraPDF',
    metaDescription:
      'Pull every transaction, opening balance, and closing balance from any bank statement PDF. Works on scans. Free tier.',
    h1: 'Bank statement parser API',
    subhead:
      'Send any bank statement PDF — checking, savings, credit card, or business. Get every transaction row plus opening and closing balance as typed JSON.',
    schema: [
      { name: 'bank_name', description: 'Issuing financial institution.' },
      { name: 'account_holder', description: 'Name on the account.' },
      { name: 'account_number_last4', description: 'Last 4 digits of the account.' },
      { name: 'statement_period_start', description: 'ISO start date of the statement window.' },
      { name: 'statement_period_end', description: 'ISO end date of the statement window.' },
      { name: 'opening_balance', description: 'Balance at start of period.' },
      { name: 'closing_balance', description: 'Balance at end of period.' },
      { name: 'total_credits', description: 'Sum of deposits in the period.' },
      { name: 'total_debits', description: 'Sum of withdrawals in the period.' },
      {
        name: 'transactions',
        description: 'Array of date, description, amount, balance, and direction values.',
      },
    ],
    sample: {
      vendorName: 'First Coast Bank',
      customerName: 'Allison Park',
      invoiceNumber: 'STMT-2026-04',
      invoiceDate: '2026-04-30',
      dueDate: '2026-04-30',
      currency: 'USD',
      subtotal: 12840.22,
      tax: 0,
      total: 12840.22,
      lineItems: [
        {
          sku: 'TX-0408',
          description: '04/08 · ACH deposit · Globex Inc · payroll',
          quantity: 1,
          unitPrice: 4250.0,
          amount: 4250.0,
        },
        {
          sku: 'TX-0412',
          description: '04/12 · Card · Bluebird Coffee · -$18.40',
          quantity: 1,
          unitPrice: -18.4,
          amount: -18.4,
        },
        {
          sku: 'TX-0415',
          description: '04/15 · Wire out · Liberty Mortgage · -$2,210.00',
          quantity: 1,
          unitPrice: -2210.0,
          amount: -2210.0,
        },
      ],
    },
    apiSnippet: {
      curl: `# 1. Upload the statement
DOC_ID=$(curl -s -X POST https://api.okrapdf.com/v1/documents \\
  -H "Authorization: Bearer $OKRA_API_KEY" \\
  -F "file=@statement.pdf" | jq -r '.id')

# 2. Extract with a bank-statement JSON Schema
curl -X POST "https://api.okrapdf.com/v1/documents/$DOC_ID/structured-output" \\
  -H "Authorization: Bearer $OKRA_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "schema": {
      "type": "object",
      "properties": {
        "bank_name": { "type": "string" },
        "account_holder": { "type": "string" },
        "opening_balance": { "type": "number" },
        "closing_balance": { "type": "number" },
        "transactions": { "type": "array" }
      }
    }
  }'`,
      node: `import okraPDF from "@okrapdf/sdk";

const okra = new okraPDF({ apiKey: process.env.OKRA_API_KEY });

const doc = await okra.documents.create({
  file: fs.createReadStream("statement.pdf"),
});
const result = await okra.documents.structuredOutput(doc.id, {
  schema: {
    type: "object",
    properties: {
      bank_name: { type: "string" },
      account_holder: { type: "string" },
      opening_balance: { type: "number" },
      closing_balance: { type: "number" },
      transactions: { type: "array" },
    },
  },
});`,
    },
    relatedLinks: [
      {
        href: '/tools/bank-statement-to-csv',
        title: 'Bank statement to CSV',
        body: 'Browser extractor for one-off statement conversions, no API key needed.',
      },
      {
        href: '/use-cases/bank-statement-extraction',
        title: 'Bank statement extraction workflow',
        body: 'How accounting teams reconcile thousands of statements with okraPDF.',
      },
      {
        href: '/tools/pdf-to-link',
        title: 'API keys and hosting',
        body: 'Mint a key, upload documents, and call extraction from your backend.',
      },
    ],
    faqs: [
      {
        q: 'Does it work on scanned statements?',
        a: 'Yes. OCR runs automatically when the PDF has no text layer. Multi-page statements are handled in one call.',
      },
      {
        q: 'Credit card statements too?',
        a: 'Yes. The same schema applies — credits and debits are signed in the transactions array.',
      },
      {
        q: 'How is this priced?',
        a: 'Extraction is priced per page. The free tier covers the first 100 pages.',
      },
    ],
  },
  {
    slug: 'w9',
    docType: 'W-9 / tax form',
    navTitle: 'W-9',
    eyebrow: 'By document type / W-9',
    outcome: 'Legal name, TIN, entity type, address, and signature pulled into vendor records.',
    metaTitle: 'W-9 Parser API - extract data from IRS Form W-9 | okraPDF',
    metaDescription:
      'Turn a signed W-9 PDF into a typed JSON record — legal name, business name, TIN, entity type, address, and signature date. One API call.',
    h1: 'W-9 parser API',
    subhead:
      'Send any signed Form W-9 PDF. Get legal name, business name, TIN, entity classification, address, and signature date as typed JSON.',
    schema: [
      { name: 'legal_name', description: 'Name as shown on the income tax return.' },
      { name: 'business_name', description: 'DBA or trade name, if different.' },
      { name: 'entity_type', description: 'Individual, sole proprietor, LLC, C-corp, S-corp, partnership, trust.' },
      { name: 'llc_tax_class', description: 'C, S, or P — only present when entity_type is LLC.' },
      { name: 'tin', description: 'Taxpayer identification number (SSN or EIN).' },
      { name: 'tin_type', description: 'ssn or ein.' },
      { name: 'address', description: 'Street address, city, state, ZIP.' },
      { name: 'exempt_payee_code', description: 'Optional exempt payee code.' },
      { name: 'signature_date', description: 'ISO date the form was signed.' },
    ],
    sample: {
      vendorName: 'Wonka Industries LLC',
      customerName: 'Globex Industries',
      invoiceNumber: 'W9-WONKA-2026',
      invoiceDate: '2026-03-04',
      dueDate: '2026-03-04',
      currency: 'USD',
      subtotal: 0,
      tax: 0,
      total: 0,
      lineItems: [
        {
          sku: 'LEGAL-NAME',
          description: 'Willy Wonka',
          quantity: 1,
          unitPrice: 0,
          amount: 0,
        },
        {
          sku: 'ENTITY',
          description: 'Limited liability company · LLC tax class C',
          quantity: 1,
          unitPrice: 0,
          amount: 0,
        },
        {
          sku: 'TIN',
          description: 'EIN ••-•••7821',
          quantity: 1,
          unitPrice: 0,
          amount: 0,
        },
      ],
    },
    apiSnippet: {
      curl: `# 1. Upload the W-9 PDF
DOC_ID=$(curl -s -X POST https://api.okrapdf.com/v1/documents \\
  -H "Authorization: Bearer $OKRA_API_KEY" \\
  -F "file=@w9.pdf" | jq -r '.id')

# 2. Extract with a W-9 JSON Schema
curl -X POST "https://api.okrapdf.com/v1/documents/$DOC_ID/structured-output" \\
  -H "Authorization: Bearer $OKRA_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "schema": {
      "type": "object",
      "properties": {
        "legal_name": { "type": "string" },
        "business_name": { "type": "string" },
        "entity_type": { "type": "string" },
        "tin": { "type": "string" },
        "signature_date": { "type": "string", "format": "date" }
      }
    }
  }'`,
      node: `import okraPDF from "@okrapdf/sdk";

const okra = new okraPDF({ apiKey: process.env.OKRA_API_KEY });

const doc = await okra.documents.create({
  file: fs.createReadStream("w9.pdf"),
});
const result = await okra.documents.structuredOutput(doc.id, {
  schema: {
    type: "object",
    properties: {
      legal_name: { type: "string" },
      business_name: { type: "string" },
      entity_type: { type: "string" },
      tin: { type: "string" },
      signature_date: { type: "string", format: "date" },
    },
  },
});`,
    },
    relatedLinks: [
      {
        href: '/tools/pdf-to-json?template=w9',
        title: 'Full PDF to JSON extractor',
        body: 'Dry-run a W-9 in the browser before wiring the API.',
      },
      {
        href: '/document-types/invoice',
        title: 'Invoice parser',
        body: 'Pair W-9 onboarding with invoice ingestion for 1099 vendors.',
      },
      {
        href: '/tools/pdf-to-link',
        title: 'API keys and hosting',
        body: 'Mint a key, upload documents, and call extraction from your backend.',
      },
    ],
    faqs: [
      {
        q: 'Does it work on scanned W-9s?',
        a: 'Yes. OCR runs automatically. The TIN field is preserved exactly — no fuzzy matching.',
      },
      {
        q: 'Does it pull the signature image?',
        a: 'No — only the signature date is extracted. Signature image and verification are out of scope.',
      },
      {
        q: 'How is this priced?',
        a: 'Extraction is priced per page. The free tier covers the first 100 pages.',
      },
    ],
  },
];

export const DOCUMENT_TYPES_BY_SLUG: Record<string, DocumentTypeMeta> =
  DOCUMENT_TYPES.reduce<Record<string, DocumentTypeMeta>>((acc, dt) => {
    acc[dt.slug] = dt;
    return acc;
  }, {});

export const DOCUMENT_TYPES_INDEX_SEO: SeoMeta = {
  title: 'PDF Parsers by Document Type - Invoice Parser API | okraPDF',
  description:
    'Pre-built PDF parser pages for common document types. Start with invoice extraction and expand into structured PDF workflows.',
  canonical: 'https://okrapdf.com/document-types',
};

export function documentTypeSeoFor(slug: string): SeoMeta | null {
  const dt = DOCUMENT_TYPES_BY_SLUG[slug];
  if (!dt) return null;
  return {
    title: dt.metaTitle,
    description: dt.metaDescription,
    canonical: `https://okrapdf.com/document-types/${dt.slug}`,
  };
}

export function documentTypeSampleJson(dt: DocumentTypeMeta): Record<string, unknown> {
  const lineItems = dt.sample.lineItems.map((item) => ({
    sku: item.sku,
    description: item.description,
    quantity: item.quantity,
    unit_price: item.unitPrice,
    amount: item.amount,
  }));

  switch (dt.slug) {
    case 'purchase-order':
      return {
        document_type: dt.slug,
        po_number: dt.sample.invoiceNumber,
        vendor_name: dt.sample.vendorName,
        buyer_name: dt.sample.customerName,
        order_date: dt.sample.invoiceDate,
        ship_date: dt.sample.dueDate,
        currency: dt.sample.currency,
        subtotal: dt.sample.subtotal,
        tax: dt.sample.tax,
        total: dt.sample.total,
        line_items: lineItems,
      };
    case 'bill-of-lading':
      return {
        document_type: dt.slug,
        bol_number: dt.sample.invoiceNumber,
        carrier: dt.sample.vendorName,
        shipper: dt.sample.vendorName,
        consignee: dt.sample.customerName,
        bol_date: dt.sample.invoiceDate,
        freight_total: dt.sample.total,
        currency: dt.sample.currency,
        line_items: lineItems,
      };
    case 'receipt':
      return {
        document_type: dt.slug,
        merchant: dt.sample.vendorName,
        transaction_id: dt.sample.invoiceNumber,
        transaction_date: dt.sample.invoiceDate,
        currency: dt.sample.currency,
        subtotal: dt.sample.subtotal,
        tax: dt.sample.tax,
        total: dt.sample.total,
        line_items: lineItems,
      };
    case 'bank-statement':
      return {
        document_type: dt.slug,
        bank_name: dt.sample.vendorName,
        account_holder: dt.sample.customerName,
        statement_id: dt.sample.invoiceNumber,
        statement_date: dt.sample.invoiceDate,
        currency: dt.sample.currency,
        closing_balance: dt.sample.total,
        transactions: dt.sample.lineItems.map((item) => ({
          id: item.sku,
          description: item.description,
          amount: item.amount,
        })),
      };
    case 'w9':
      return {
        document_type: dt.slug,
        legal_name: 'Willy Wonka',
        business_name: dt.sample.vendorName,
        entity_type: 'LLC',
        llc_tax_class: 'C',
        tin_type: 'ein',
        tin_last4: '7821',
        signature_date: dt.sample.invoiceDate,
      };
    case 'invoice':
    default:
      return {
        document_type: dt.slug,
        vendor_name: dt.sample.vendorName,
        customer_name: dt.sample.customerName,
        invoice_number: dt.sample.invoiceNumber,
        invoice_date: dt.sample.invoiceDate,
        due_date: dt.sample.dueDate,
        currency: dt.sample.currency,
        subtotal: dt.sample.subtotal,
        tax: dt.sample.tax,
        total: dt.sample.total,
        line_items: lineItems,
      };
  }
}
